import { FlashtoolsFeature } from '../types';
import { NavIconName } from './components/icons';
import { V2_BASE, v2Path } from './nav';

/**
 * The single v2 route table.
 *
 * The sidebar, the command palette and NewUiApp's `<Routes>` block all read
 * from here, so a route can never exist without a way to reach it — and a nav
 * item can never point at a route that does not exist.
 */

export type NavSectionId = 'find' | 'inventory' | 'platforms' | 'account';

/** The gate strings App.tsx already passes to `FeatureGate`, verbatim. */
export interface RouteGate {
  feature: FlashtoolsFeature;
  title: string;
  description: string;
  requiredTier: string;
  /** Chip shown beside a locked nav item. */
  tierChip: string;
}

export interface V2RouteDef {
  /** Stable id used to look up the screen component. */
  id: string;
  /** Absolute v2 path. */
  path: string;
  /** i18n key for the label — never hardcode a nav label. */
  labelKey: string;
  /** Which sidebar section this belongs to; `undefined` = not a nav item. */
  section?: NavSectionId;
  icon: NavIconName;
  gate?: RouteGate;
  /** Listed in the command palette. */
  inPalette: boolean;
  /** True for public v1 routes v2 links out to rather than rebuilding. */
  external?: boolean;
  /**
   * Hidden from the nav entirely when the gate denies access, instead of the
   * usual shown-as-locked treatment. For invite-only surfaces that should not
   * be discoverable at all (v1's hide-completely behaviour, per item).
   */
  hideWhenLocked?: boolean;
  /**
   * CrowdVolt staging surface: hidden from the nav for everyone outside
   * `CROWDVOLT_CONNECT_ALLOWLIST`. The route stays mounted, so accounts that
   * already use CrowdVolt keep every deep link and in-page flow.
   */
  staged?: boolean;
  /**
   * Reachable by a team member. Absent means NO — the same
   * deny-by-default rule the backend uses, for the same reason: a surface
   * added later should be invisible to a team member until someone decides
   * otherwise, rather than exposed by an omission.
   *
   * This is cosmetic. It decides what a team member is SHOWN; what they may DO is
   * enforced per endpoint by the capability on each view.
   */
  seatVisible?: boolean;
  /** Shown with a "Beta" chip beside its label in the nav and palette. */
  beta?: boolean;
}

export const WAITLIST_GATE: RouteGate = {
  feature: 'seller_waitlists',
  title: 'Seller Waitlists',
  description: 'See sold-out shows and join waitlists for hot cities.',
  requiredTier: 'Pro or Reserve',
  tierChip: 'Pro',
};

export const DROPS_GATE: RouteGate = {
  feature: 'buying_tools',
  title: 'Buying Tools',
  description: 'Flash Buy auto-purchases tickets the moment they release.',
  requiredTier: 'Reserve',
  tierChip: 'Reserve',
};

/**
 * Nine primary items in three sections, then the four footer items.
 * Order inside each section preserves v1's left-to-right nav order exactly.
 */
export const V2_ROUTES: readonly V2RouteDef[] = [
  // ── FIND ───────────────────────────────────────────────────────────────────
  {
    id: 'notifications',
    path: v2Path('/notifications'),
    labelKey: 'app.navigation.notifications',
    section: 'find',
    icon: 'bell',
    inPalette: true,
  },
  {
    id: 'waitlist',
    path: v2Path('/waitlist'),
    labelKey: 'app.navigation.waitlist',
    section: 'find',
    icon: 'list',
    gate: WAITLIST_GATE,
    inPalette: true,
  },
  {
    id: 'upcoming-sales',
    path: v2Path('/upcoming-sales'),
    labelKey: 'app.navigation.upcomingSales',
    section: 'find',
    icon: 'calendar',
    inPalette: true,
    beta: true,
    // Deliberately NOT seatVisible. The endpoint would serve a team member, but
    // seat visibility is what a team member was sold, not what the API permits
    // — so a new surface stays invisible to them until someone decides
    // otherwise. seatRoutes.test.ts pins that list.
  },
  {
    id: 'drops',
    path: v2Path('/drops'),
    labelKey: 'app.navigation.drops',
    section: 'find',
    icon: 'bolt',
    gate: DROPS_GATE,
    inPalette: true,
    // Invite-only: not discoverable unless the account is entitled or comped.
    hideWhenLocked: true,
    beta: true,
  },
  // ── INVENTORY ──────────────────────────────────────────────────────────────
  {
    id: 'tickets',
    seatVisible: true,
    path: v2Path('/tickets'),
    labelKey: 'app.navigation.tickets',
    section: 'inventory',
    icon: 'ticket',
    inPalette: true,
  },
  {
    id: 'listings',
    path: v2Path('/listings'),
    labelKey: 'app.navigation.listings',
    section: 'inventory',
    icon: 'tag',
    inPalette: true,
    staged: true,
  },
  {
    id: 'transfers',
    seatVisible: true,
    path: v2Path('/transfers'),
    labelKey: 'app.navigation.transfers',
    section: 'inventory',
    icon: 'transfer',
    inPalette: true,
  },
  // ── PLATFORMS ──────────────────────────────────────────────────────────────
  {
    id: 'accounts',
    seatVisible: true,
    path: v2Path('/accounts'),
    labelKey: 'app.navigation.connections',
    section: 'platforms',
    icon: 'link',
    inPalette: true,
  },
  {
    id: 'dice-settings',
    path: v2Path('/dice/settings'),
    labelKey: 'app.navigation.dice',
    section: 'platforms',
    icon: 'phone',
    inPalette: true,
  },
  // ── ACCOUNT (sidebar footer zone) ──────────────────────────────────────────
  {
    id: 'profile',
    path: v2Path('/profile'),
    labelKey: 'app.navigation.profile',
    section: 'account',
    icon: 'user',
    inPalette: true,
  },
  {
    id: 'billing',
    path: v2Path('/billing'),
    labelKey: 'app.navigation.billing',
    section: 'account',
    icon: 'card',
    inPalette: true,
  },
  {
    // Owner-only by omission of `seatVisible` — a team member cannot manage
    // the team they are on, and the endpoints behind this refuse them too.
    id: 'seats',
    path: v2Path('/seats'),
    labelKey: 'app.navigation.seats',
    section: 'account',
    icon: 'users',
    inPalette: true,
  },
  {
    id: 'setup',
    path: v2Path('/setup'),
    labelKey: 'app.navigation.setup',
    section: 'account',
    icon: 'book',
    inPalette: true,
    staged: true,
  },
  {
    // Support / bug request form. Visible to seats: a team member hitting a
    // wall is exactly who should be able to say so. Also on the billing
    // bypass list — see V2_BILLING_BYPASS_PATHS.
    id: 'support',
    seatVisible: true,
    path: v2Path('/support'),
    labelKey: 'app.navigation.support',
    section: 'account',
    icon: 'lifebuoy',
    inPalette: true,
  },
  {
    // Public v1 route, not rebuilt in v2. v2Path('/help') === '/help'.
    id: 'help',
    path: v2Path('/help'),
    labelKey: 'app.navigation.help',
    section: 'account',
    icon: 'help',
    inPalette: true,
    external: true,
  },
  // ── Not nav items ──────────────────────────────────────────────────────────
  {
    // Reached from the Dice Accounts page header, and from the palette.
    id: 'dice-signup',
    path: v2Path('/dice/signup'),
    labelKey: 'app.navigation.diceSignup',
    icon: 'plus',
    inPalette: true,
  },
  {
    // Stripe return URL. You cannot usefully navigate to it, so it is not in
    // the palette and not a nav item.
    id: 'dice-success',
    path: v2Path('/dice/success'),
    labelKey: 'app.navigation.dice',
    icon: 'phone',
    inPalette: false,
  },
];

export const SECTION_ORDER: readonly NavSectionId[] = [
  'find',
  'inventory',
  'platforms',
  'account',
];

export const SECTION_LABEL_KEYS: Record<NavSectionId, string> = {
  find: 'app.navigation.sections.find',
  inventory: 'app.navigation.sections.inventory',
  platforms: 'app.navigation.sections.platforms',
  account: 'app.navigation.sections.account',
};

/** The two micro-links under the sidebar footer. */
export const V2_MICRO_LINKS: readonly { labelKey: string; path: string }[] = [
  { labelKey: 'app.navigation.privacy', path: v2Path('/privacy') },
  { labelKey: 'app.navigation.terms', path: v2Path('/terms') },
];

/** Default landing route inside the shell. */
export const V2_DEFAULT_ROUTE = v2Path('/notifications');

/** Path relative to `/v2/app/*`, for use inside NewUiApp's nested `<Routes>`. */
export function relativePath(absolute: string): string {
  return absolute.startsWith(`${V2_BASE}/`) ? absolute.slice(V2_BASE.length + 1) : absolute;
}

export function routesInSection(section: NavSectionId): V2RouteDef[] {
  return V2_ROUTES.filter((r) => r.section === section);
}

/**
 * The routes a seat member may reach.
 *
 * Derived from `seatVisible` rather than listed separately, so the nav filter
 * and the deep-link guard can never disagree about what a team member can see — the
 * bug that would otherwise show a team member a nav item that redirects away the
 * instant they click it.
 */
export function seatVisibleRoutes(): V2RouteDef[] {
  return V2_ROUTES.filter((r) => r.seatVisible);
}

/** Where a team member lands: the first surface they are allowed to see. */
export const SEAT_LANDING_ROUTE = v2Path('/tickets');

/** Whether a team member may sit on this pathname. Prefix match, so deep links work. */
export function isSeatVisiblePath(pathname: string): boolean {
  return seatVisibleRoutes().some((r) => pathname.startsWith(r.path));
}
